// De vaste opbouw van elke kennisbankpagina (huisregels 5–10,
// docs/input/frontend/opschonen/huisregels-nieuwe-weergave.md): kop met één
// zin, statische kerngetallen, de filterbalk met het zoekveld vooraan (ronde 5:
// zoals live, Clipy hooc06uw015x 2:10) en pillen, en het sorteren buiten de
// filters met een i per optie. Eén keer gebouwd, zodat Skills, Trends,
// Bronnen, Arbeidsmarkt en Funding er hetzelfde uitzien en hetzelfde werken.
// Overgenomen uit nieuw/kennis/gedeeld.tsx (afr-kennis) en aangevuld.

import { ArrowUpDown, Check, Maximize2, Minimize2, PanelRightClose, PanelRightOpen, Search, X } from "lucide-react";
import type { ReactNode } from "react";

import { Chip, Icoon, Knop, LegeStaat } from "./basis";
import { Info } from "./lagen";
import { Menu } from "./menu";
import type { PaneelBreedte } from "./paneelbreedte";

/** Titel en één zin uitleg; rechts eventueel een knop. Statisch. */
export function LijstKop({ titel, uitleg, rechts }: { titel: ReactNode; uitleg?: ReactNode; rechts?: ReactNode }) {
  return (
    <header className="l-kop">
      <div className="l-kop__tekst">
        <h1 className="l-kop__titel">{titel}</h1>
        {uitleg && <p className="s-meta">{uitleg}</p>}
      </div>
      {rechts}
    </header>
  );
}

/** Het zoekveld van de pagina: een echt veld dat geen pil maakt. Hoort vooraan
 *  in de FilterBalk (prop `zoek`); daar is het smal en wordt het breed zodra je
 *  erin klikt of iets hebt getypt. */
export function ZoekInLijst({ waarde, onWaarde, plaatshouder, aantal }: { waarde: string; onWaarde: (v: string) => void; plaatshouder: string; /** Aantal treffers, getoond zolang er gezocht wordt. */ aantal?: number }) {
  return (
    <label className="l-zoek" data-gevuld={waarde ? true : undefined}>
      <Icoon als={Search} />
      <span className="s-verborgen">{plaatshouder}</span>
      <input type="search" value={waarde} placeholder={plaatshouder} onChange={(e) => onWaarde(e.target.value)} onKeyDown={(e) => e.key === "Escape" && waarde && (e.stopPropagation(), onWaarde(""))} />
      {waarde && typeof aantal === "number" && <span className="l-zoek__tel">{aantal.toLocaleString("nl-NL")}</span>}
      {waarde && (
        <button type="button" className="l-zoek__wis" aria-label="Zoeken wissen" onClick={() => onWaarde("")}>
          <Icoon als={X} klein />
        </button>
      )}
    </label>
  );
}

export type FilterPil = { sleutel: string; label: string; weg: () => void };

/** De filterbalk: vooraan het zoekveld (`zoek`, een ZoekInLijst), dan menu's
 *  met vinkjes; daaronder de gekozen filters als pillen, "Alles wissen" vanaf
 *  twee. Sorteren staat hier niet in. */
export function FilterBalk({ children, pillen, onWis, zoek }: { children: ReactNode; pillen: readonly FilterPil[]; onWis: () => void; zoek?: ReactNode }) {
  return (
    <section className="l-filterbalk" aria-label="Zoeken en filters">
      <div className="l-filterbalk__menus">
        {zoek}
        {children}
      </div>
      {pillen.length > 0 && (
        <div className="l-filterbalk__pillen">
          {pillen.map((p) => (
            <Chip key={p.sleutel} onWeg={p.weg}>
              {p.label}
            </Chip>
          ))}
          {pillen.length > 1 && (
            <Knop variant="link" onClick={onWis}>
              Alles wissen
            </Knop>
          )}
        </div>
      )}
    </section>
  );
}

export type SorteerOptie<T extends string> = { waarde: T; label: string; /** Wat deze volgorde doet; verschijnt bij de i naast de optie. */ uitleg: string };

/** Sorteren, buiten de filters, rechts boven de lijst. Elke optie is één woord
 *  met een kleine i; zweven over de i legt uit wat ze doet (ronde 5, Clipy
 *  hooc06uw015x 3:04–3:27: zoals de i op de grafieken, geen tekst eronder). */
export function Sorteer<T extends string>({ waarde, opties, onWaarde }: { waarde: T; opties: readonly SorteerOptie<T>[]; onWaarde: (v: T) => void }) {
  const huidig = opties.find((o) => o.waarde === waarde) ?? opties[0]!;
  return (
    <div className="l-sorteer">
      <Menu
        label={`Sorteren: ${huidig.label}. ${huidig.uitleg}`}
        inhoud={
          <>
            <Icoon als={ArrowUpDown} klein />
            <span>{huidig.label}</span>
          </>
        }
      >
        {(sluit) => (
          <div className="l-sorteer__lijst" role="menu" aria-label="Sorteren">
            {opties.map((o) => (
              <div key={o.waarde} className="l-sorteer__optie" role="none">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={o.waarde === waarde}
                  aria-description={o.uitleg}
                  className="l-sorteer__kies"
                  onClick={() => {
                    onWaarde(o.waarde);
                    sluit();
                  }}
                >
                  <span className="l-sorteer__vink">{o.waarde === waarde && <Icoon als={Check} klein />}</span>
                  <span className="l-sorteer__label">{o.label}</span>
                </button>
                <Info tekst={o.uitleg} label={`Uitleg bij ${o.label}`} />
              </div>
            ))}
          </div>
        )}
      </Menu>
    </div>
  );
}

/** De regel boven de lijst: links weergaven of een telling, rechts sorteren. */
export function LijstBalk({ links, rechts }: { links?: ReactNode; rechts?: ReactNode }) {
  return (
    <div className="l-lijstbalk">
      <div className="l-lijstbalk__links">{links}</div>
      {rechts}
    </div>
  );
}

/** De knoppen in de kop van elk paneel: volle breedte en inklappen. */
export function PaneelKnoppen({ p }: { p: PaneelBreedte }) {
  return (
    <>
      <Knop variant="icoon" aria-label={p.vol ? "Paneel smaller" : "Paneel volle breedte"} title={p.vol ? "Paneel smaller" : "Paneel volle breedte"} icoon={p.vol ? Minimize2 : Maximize2} onClick={p.wisselVol} />
      <Knop variant="icoon" aria-label="Paneel inklappen" title="Paneel inklappen" icoon={PanelRightClose} onClick={p.verberg} />
    </>
  );
}

/** Een ingeklapt paneel: een knop rechtsboven om het terug te halen. */
export function PaneelTab({ titel, onToon }: { titel: string; onToon: () => void }) {
  return (
    <button type="button" className="l-paneeltab" onClick={onToon} aria-label={`Paneel tonen: ${titel}`}>
      <Icoon als={PanelRightOpen} />
      <span className="l-paneeltab__tekst">{titel}</span>
    </button>
  );
}

/** Laden: lege regels op de plek van de lijst. */
export function LaadRegels({ regels = 6, zin = "laden…" }: { regels?: number; zin?: string }) {
  return (
    <div className="l-laden" role="status" aria-label={zin}>
      {Array.from({ length: regels }, (_, i) => (
        <span key={i} className="l-laden__regel" />
      ))}
      <span className="s-verborgen">{zin}</span>
    </div>
  );
}

/** Fout: wat er misging en opnieuw proberen. */
export function Fout({ wat, reden, onOpnieuw }: { wat: string; reden: string; onOpnieuw?: () => void }) {
  return <LegeStaat zin={`${wat} konden niet worden opgehaald: ${reden}`} actie={onOpnieuw ? "Opnieuw proberen" : undefined} onActie={onOpnieuw} />;
}
